import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CaretLeft, CaretRight, UsersThree } from "@phosphor-icons/react";

const DAY_NAMES = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTH_NAMES = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const pad = (n) => String(n).padStart(2, "0");

function monthCells(year, month) {
  const first = new Date(year, month, 1);
  const offset = (first.getDay() + 6) % 7;
  const total = new Date(year, month + 1, 0).getDate();
  const cells = Array(offset).fill(null);
  for (let d = 1; d <= total; d++) cells.push(`${year}-${pad(month + 1)}-${pad(d)}`);
  while (cells.length % 7) cells.push(null);
  return cells;
}

function dayTone(info) {
  if (info?.free_count > 0) return { cls: "bg-emerald-50 border-emerald-300 hover:bg-emerald-100", dot: "bg-emerald-500", label: "disponibile" };
  if (info?.booked_count > 0) return { cls: "bg-amber-50 border-amber-300 hover:bg-amber-100", dot: "bg-amber-500", label: "tutto prenotato" };
  return { cls: "bg-slate-50 border-slate-200 hover:bg-slate-100", dot: "bg-slate-300", label: "nessuna disponibilità" };
}

function DayDetail({ date, info, operators }) {
  const byOp = info?.slots_by_operator || {};
  const label = new Date(`${date}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  return (
    <div className="space-y-3" data-testid={`admin-avail-detail-${date}`}>
      <div className="font-semibold text-sm capitalize">{label}</div>
      <div className="flex gap-3 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Libero</span>
        <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Prenotato</span>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {operators.map((op) => {
          const slots = byOp[op.id] || [];
          return (
            <div key={op.id} className="border-b border-slate-100 pb-2 last:border-0" data-testid={`admin-avail-op-${date}-${op.id}`}>
              <div className="text-xs font-semibold text-slate-800">{op.name}</div>
              {slots.length === 0 ? (
                <div className="text-xs text-slate-400 italic">Nessuna disponibilità</div>
              ) : (
                <div className="flex flex-wrap gap-1 mt-1">
                  {slots.map((s) => (
                    <span
                      key={s.time}
                      className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${s.booked ? "bg-amber-100 text-amber-800 line-through decoration-amber-500/60" : "bg-emerald-100 text-emerald-800"}`}
                      title={s.booked ? "Prenotato" : "Libero"}
                    >
                      {s.time}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayCell({ date, info, operators, openDate, setOpenDate, pinned, setPinned }) {
  const closeTimer = useRef(null);
  const tone = dayTone(info);
  const open = openDate === date;
  const enter = () => { clearTimeout(closeTimer.current); if (!pinned) setOpenDate(date); };
  const leave = () => { if (!pinned) closeTimer.current = setTimeout(() => setOpenDate((d) => (d === date ? null : d)), 150); };
  return (
    <Popover open={open} onOpenChange={(v) => { if (!v) { setOpenDate(null); setPinned(false); } }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onMouseEnter={enter} onMouseLeave={leave}
          onClick={() => { setOpenDate(date); setPinned(true); }}
          className={`h-16 rounded-md border text-left p-1.5 transition-colors ${tone.cls}`}
          data-testid={`admin-avail-day-${date}`}
          aria-label={`${date}: ${tone.label}`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">{Number(date.slice(8))}</span>
            <span className={`w-2.5 h-2.5 rounded-full ${tone.dot}`} />
          </div>
          {(info?.free_count > 0 || info?.booked_count > 0) && (
            <div className="text-[10px] text-slate-600 mt-1 leading-tight">
              {info.free_count > 0 && <div>{info.free_count} {info.free_count === 1 ? "libero" : "liberi"}</div>}
              {info.booked_count > 0 && <div className="text-amber-700">{info.booked_count} {info.booked_count === 1 ? "prenotato" : "prenotati"}</div>}
            </div>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" onMouseEnter={enter} onMouseLeave={leave}>
        <DayDetail date={date} info={info} operators={operators} />
      </PopoverContent>
    </Popover>
  );
}

export function AdminAvailabilityOverview() {
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [data, setData] = useState({ operators: [], days: {} });
  const [loading, setLoading] = useState(false);
  const [openDate, setOpenDate] = useState(null);
  const [pinned, setPinned] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/calendar/admin-overview?year=${cursor.year}&month=${cursor.month + 1}`)
      .then((r) => setData(r.data || { operators: [], days: {} }))
      .catch(() => setData({ operators: [], days: {} }))
      .finally(() => setLoading(false));
  }, [cursor]);

  const cells = useMemo(() => monthCells(cursor.year, cursor.month), [cursor]);
  const shift = (d) => setCursor((c) => {
    const m = c.month + d;
    return { year: c.year + Math.floor(m / 12), month: ((m % 12) + 12) % 12 };
  });

  return (
    <section className="kpi-card mt-8" data-testid="admin-availability-overview">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <UsersThree size={22} weight="duotone" className="text-emerald-700" />
          <h2 className="font-display text-lg font-semibold">Disponibilità Operatori</h2>
          <span className="text-xs text-slate-500">({data.operators.length} operatori)</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="admin-avail-prev"><CaretLeft size={14} /></Button>
          <span className="font-semibold text-sm w-36 text-center" data-testid="admin-avail-month">{MONTH_NAMES[cursor.month]} {cursor.year}</span>
          <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="admin-avail-next"><CaretRight size={14} /></Button>
        </div>
      </div>
      <div className="flex gap-4 text-xs text-slate-500 mb-3">
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Almeno un orario libero</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Solo orari prenotati</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-slate-300" /> Nessuno disponibile</span>
      </div>
      <div className={`grid grid-cols-7 gap-1.5 ${loading ? "opacity-50" : ""}`}>
        {DAY_NAMES.map((d) => <div key={d} className="text-[11px] font-semibold text-slate-500 text-center">{d}</div>)}
        {cells.map((date, i) => date ? (
          <DayCell
            key={date} date={date} info={data.days[date]} operators={data.operators}
            openDate={openDate} setOpenDate={setOpenDate} pinned={pinned} setPinned={setPinned}
          />
        ) : <div key={`e${i}`} />)}
      </div>
    </section>
  );
}
